import { ChangeDetectionStrategy, Component, TemplateRef, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { AnalysisColumnVisibility, AnalysisTable } from '@shared/components/analysis-table/analysis-table';
import { ColumnDef, TableToolbarAction } from '@shared/components/data-table/data-table';
import { StatePanel } from '@shared/components/state-panel/state-panel';
import { Analysis } from '@shared/models/analysis.model';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { AnalysisStore } from '../../store/analysis-store';
import { CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { CampaignsService } from '../../services/campaigns.service';
import { ExcludeReason } from '../exclude-reason/exclude-reason';
import { FilterRail, RailGroup } from '@shared/components/filter-rail/filter-rail';
import { StageChip } from '../stage-chip/stage-chip';
import { PHASES, STAGE_META, WorkItem, WorkflowStage, marksOf, stepsOf, stepsTitle } from '../../models/campaign-workflow.model';

const ALL = 'all';
const COLUMNS: AnalysisColumnVisibility = {
  analysis: true, type: false, address: false, consumption: false, status: false, source: false, createdAt: false, execution: false,
};
const MARKS: { id: string; label: string; test: (item: WorkItem) => boolean }[] = [
  { id: 'corrected', label: 'Korrigiert', test: (item) => item.corrections > 0 },
  { id: 'replanned', label: 'Nachgeplant', test: (item) => item.replanned },
  { id: 'no_planning', label: 'Ohne Planung', test: (item) => item.planning.outcome === 'none' },
  { id: 'planning_failed', label: 'Planung fehlgeschlagen', test: (item) => item.planning.outcome === 'failed' },
];

/** All analyses of the campaign: the Hub's analysis table, with the stages in a rail beside it. */
@Component({
  selector: 'app-campaign-analyses-tab',
  standalone: true,
  imports: [DatePipe, DecimalPipe, NgIconComponent, HlmButtonDirective, AnalysisTable, StatePanel, ExcludeReason, FilterRail, StageChip],
  providers: [provideIcons({ lucideX })],
  templateUrl: './campaign-analyses-tab.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignAnalysesTab {
  readonly store = inject(CampaignWorkflowStore);
  private readonly analysisStore = inject(AnalysisStore);
  private readonly api = inject(CampaignsService);
  private readonly org = inject(CurrentOrganizationStore);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  readonly columnVisibility = COLUMNS;
  readonly isLoading = this.analysisStore.isLoading;
  readonly table = viewChild(AnalysisTable);
  readonly stageTpl = viewChild.required<TemplateRef<unknown>>('stageTpl');
  readonly stepsTpl = viewChild.required<TemplateRef<unknown>>('stepsTpl');
  readonly noteTpl = viewChild.required<TemplateRef<unknown>>('noteTpl');
  readonly kwpTpl = viewChild.required<TemplateRef<unknown>>('kwpTpl');
  readonly paybackTpl = viewChild.required<TemplateRef<unknown>>('paybackTpl');
  readonly changedTpl = viewChild.required<TemplateRef<unknown>>('changedTpl');

  readonly stage = signal<WorkflowStage | null>(null);
  readonly runId = signal<string | null>(null);
  readonly runMembers = signal<Set<string> | null>(null);
  readonly busy = signal(false);

  readonly mark = signal<string | null>(null);
  readonly active = computed(() => [this.stage() ?? ALL, ...(this.mark() ? [this.mark()!] : [])]);
  readonly groups = computed<RailGroup[]>(() => {
    const counts = this.store.counts();
    return [
      { rows: [{ id: ALL, label: 'Alle Analysen', count: this.store.total() }] },
      ...PHASES.map((phase) => ({
        label: phase.name,
        sum: phase.stages.reduce((all, stage) => all + (counts?.[stage] ?? 0), 0),
        rows: phase.stages.map((stage) => ({ id: stage, label: STAGE_META[stage].label, count: counts?.[stage] ?? 0, color: STAGE_META[stage].color, tip: STAGE_META[stage].tip })),
      })),
      { label: 'Merkmal', rows: MARKS.map(({ id, label, test }) => ({ id, label, count: (this.store.items() ?? []).reduce((all, item) => all + (test(item) ? 1 : 0), 0) })) },
    ];
  });

  /** What the rail and the execution chip narrow the table to. */
  readonly restrict = computed<Set<string> | null>(() => {
    const stage = this.stage();
    const members = this.runMembers();
    const test = MARKS.find(({ id }) => id === this.mark())?.test;
    if (!stage && !test) return members;
    const ids = (this.store.items() ?? [])
      .filter((item) => (!stage || item.stage === stage) && (!test || test(item)) && (!members || members.has(item.id)))
      .map((item) => item.id);
    return new Set(ids);
  });

  readonly runLabel = computed(() => {
    const run = this.store.runs().find((candidate) => candidate.runId === this.runId());
    return run ? new Date(run.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
  });

  private readonly item = (analysis: Analysis): WorkItem | undefined => this.store.itemById().get(analysis.id ?? '');
  readonly itemOf = this.item;
  readonly dots = (item: WorkItem) => stepsOf(item);
  readonly stepTitle = (item: WorkItem) => stepsTitle(item);
  readonly note = (item: WorkItem) => marksOf(item).join(' · ');

  readonly columns = computed<ColumnDef<Analysis>[]>(() => [
    { header: 'Stufe', template: this.stageTpl(), class: 'w-[120px]', sortValue: (a) => this.item(a)?.stage ?? null },
    { header: 'Schritte', template: this.stepsTpl(), class: 'w-[60px]' },
    { header: 'Hinweis', template: this.noteTpl(), searchValue: (a) => { const item = this.item(a); return item ? marksOf(item).join(' ') : ''; } },
    { header: 'Leistung', template: this.kwpTpl(), class: 'w-[88px] text-right', sortValue: (a) => this.item(a)?.planning.kwp ?? null },
    { header: 'Amort.', template: this.paybackTpl(), class: 'hidden w-[72px] text-right xl:table-cell', sortValue: (a) => this.item(a)?.planning.payback ?? null },
    { header: 'Geändert', template: this.changedTpl(), class: 'hidden w-[104px] text-right 2xl:table-cell', sortValue: (a) => this.item(a)?.updatedAt ?? null },
  ]);

  readonly toolbarActions = computed<TableToolbarAction[]>(() => [
    ...(this.org.can('campaign', 'export') ? [
      { label: 'Exportieren', children: [
        { label: 'Excel', action: () => void this.export('excel') },
        { label: 'CSV', action: () => void this.export('csv') },
      ] },
      { label: 'Dateien herunterladen', action: () => this.downloadFiles() },
    ] : []),
    { label: 'CSV-Vorlage herunterladen', action: () => void this.downloadTemplate(), dividerBefore: true },
  ]);

  readonly selectedAnalyses = computed(() => this.table()?.dataTableRef()?.selectedObjects() ?? []);
  readonly selectedIds = computed(() => this.selectedAnalyses().map((analysis) => analysis.id).filter((id): id is string => !!id));

  readonly rowOpen = (analysis: Analysis): void => {
    const item = this.item(analysis);
    if (item?.stage === 'done') void this.store.go('plannings', { focus: item.id });
    else if (item && ['review', 'seen', 'flagged', 'replan'].includes(item.stage)) void this.store.go('review', { stage: item.stage, focus: item.id });
    else void this.router.navigate(['/organizations', this.org.organization()?.slug, 'analyses', analysis.analysisId]);
  };

  constructor() {
    void this.store.loadItems();
    effect(() => {
      const nav = this.store.nav();
      untracked(() => {
        this.stage.set(nav.stage && nav.stage !== 'recheck' ? nav.stage : null);
        void this.applyRun(nav.runId ?? null);
      });
    });
  }

  private async applyRun(runId: string | null): Promise<void> {
    this.runId.set(runId);
    this.runMembers.set(null);
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!runId || !org || !campaign) return;
    try {
      this.runMembers.set(new Set(await this.api.getBulkRunAnalysisIds(org, campaign, runId)));
    } catch {
      this.toast.error('Die Analysen dieser Berechnung konnten nicht geladen werden.');
    }
  }

  applyRunClear(): void {
    void this.applyRun(null);
  }

  pick(id: string): void {
    if (MARKS.some((mark) => mark.id === id)) this.mark.update((current) => (current === id ? null : id));
    else this.stage.set(id === ALL ? null : (id as WorkflowStage));
  }

  clearSelection(): void {
    this.table()?.dataTableRef()?.clearSelection();
  }

  /** Go through exactly these, in the order of the table. */
  reviewSelection(): void {
    void this.store.go('review', { ids: this.selectedIds() });
  }

  runSelection(): void {
    const ids = this.selectedIds();
    const stage = this.stage();
    this.store.openRun({ ids, ...(stage ? { target: stage } : {}), onStart: () => this.clearSelection() });
  }

  async excludeSelection(reason: string): Promise<void> {
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!org || !campaign) return;
    this.busy.set(true);
    try {
      const ids = this.selectedIds();
      // The server takes a thousand at a time.
      let updated = 0;
      for (let i = 0; i < ids.length; i += 1000) {
        const result = await this.api.reviewMany(org, campaign, { ids: ids.slice(i, i + 1000), decision: 'excluded', reason });
        updated += result.updated;
      }
      this.toast.success(`${updated.toLocaleString('de-DE')} zurückgestellt.`);
      this.clearSelection();
      await this.store.refresh();
      await this.store.loadItems();
    } catch (error) {
      this.toast.error(error instanceof Error ? error.message : 'Zurückstellen ist fehlgeschlagen.');
    } finally {
      this.busy.set(false);
    }
  }

  async onQuickAction(event: { type: 'publish' | 'unpublish'; analyses: Analysis[] }): Promise<void> {
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!org || !campaign || !event.analyses.length) return;
    const enabled = event.type === 'publish';
    try {
      const hubAnalysisIds = event.analyses.map((analysis) => analysis.id ?? analysis.analysisId);
      const result = await this.api.bulkPublish(org, campaign, { hubAnalysisIds, enabled });
      this.toast.success(`${result.succeeded} Analysen ${enabled ? 'veröffentlicht' : 'nicht veröffentlicht'}.`);
      void this.analysisStore.refreshStats();
    } catch {
      this.toast.error('Veröffentlichung fehlgeschlagen.');
    }
  }

  reloadAnalyses(): void {
    void this.analysisStore.refreshStats();
    void this.store.refresh();
  }

  private downloadFiles(): void {
    const table = this.table();
    const dataTable = table?.dataTableRef();
    if (!table || !dataTable) return;
    table.openDownloadDialog(dataTable.selectedCount() > 0 ? dataTable.selectedObjects() : dataTable.limitedData());
  }

  private async export(format: 'csv' | 'excel'): Promise<void> {
    const org = this.store.organizationId();
    const campaign = this.store.campaignId();
    if (!org || !campaign) return;
    try {
      await this.api.exportCampaign(org, campaign, format);
    } catch {
      this.toast.error('Export fehlgeschlagen.');
    }
  }

  private async downloadTemplate(): Promise<void> {
    const org = this.store.organizationId();
    if (!org) return;
    try {
      await this.api.downloadCsvTemplate(org);
    } catch {
      this.toast.error('CSV-Vorlage konnte nicht heruntergeladen werden.');
    }
  }
}
